import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LifeBuoy, ChevronLeft, ChevronDown, Search, Network, MonitorSmartphone, GraduationCap, Store, CreditCard, UserCog, Headphones, BookOpen, FileCode2 } from "lucide-react";

const CATEGORIAS = [
  {
    key: "general", label: "Cuenta y primeros pasos", Icon: UserCog, color: "#1E3A8A",
    faqs: [
      { q: "¿Qué es ConstruLink?", a: "ConstruLink es un ecosistema integral para la industria de la construcción con 5 pilares: Red (conexión profesional), Software, Academia, Marketplace de recursos digitales e IA." },
      { q: "¿Cómo completo mi perfil?", a: "Ve a «Mi Perfil» desde el menú superior. Completa tus datos globales, tu perfil profesional y tu portafolio para aumentar tu visibilidad y confianza dentro de la Familia ConstruLink." },
      { q: "¿Puedo tener más de un rol?", a: "Sí. Desde «Mi Perfil → Roles» puedes activar varios perfiles (profesional, contratista, ferretería) y marcar uno como principal." },
    ],
  },
  {
    key: "red", label: "ConstruLink Red", Icon: Network, color: "#2563EB",
    faqs: [
      { q: "¿Cómo publico una oportunidad o servicio?", a: "Entra a Red → Publicar, completa el formulario y publícalo. Aparecerá en el buscador para toda la comunidad." },
      { q: "¿Cómo contacto a otro miembro?", a: "Desde el perfil o una publicación pulsa «Mensaje» para abrir una conversación en tu bandeja de Mensajes." },
    ],
  },
  {
    key: "software", label: "Software", Icon: MonitorSmartphone, color: "#0891B2",
    faqs: [
      { q: "¿Cómo descargo un software adquirido?", a: "Tras la compra, ve a Software → Mis descargas. Las descargas están protegidas y vinculadas a tu cuenta." },
      { q: "¿Las licencias tienen vencimiento?", a: "Depende del producto. Revisa la ficha del software y tu sección de Pagos y Facturación para ver el detalle de tu licencia." },
    ],
  },
  {
    key: "academia", label: "Academia", Icon: GraduationCap, color: "#7E22CE",
    faqs: [
      { q: "¿Dónde veo mis cursos?", a: "En Academia → Mis cursos encontrarás tu progreso, lecciones y materiales de cada curso inscrito." },
      { q: "¿Cómo obtengo mi certificado?", a: "Al completar el 100% de un curso con certificado, se genera automáticamente tu certificado PDF en Academia → Mis certificados, verificable por código." },
    ],
  },
  {
    key: "marketplace", label: "Marketplace", Icon: Store, color: "#D97706",
    faqs: [
      { q: "¿Cómo vendo en el Marketplace?", a: "Crea tu tienda desde Marketplace → Crear tienda. Tras la aprobación podrás publicar productos digitales y cursos, que pasan por moderación antes de estar visibles." },
      { q: "¿Cómo solicito un reembolso?", a: "En «Mis Compras» pulsa «Solicitar reembolso». Procede solo dentro de los 3 días de la compra y si aún no descargaste el material. La devolución del dinero se realiza en un máximo de 30 días." },
      { q: "¿Cuándo puedo retirar mis ganancias?", a: "En Marketplace → Comisiones verás tu saldo disponible y podrás solicitar un retiro, que el administrador revisará y pagará." },
    ],
  },
  {
    key: "pagos", label: "Pagos y Membresías", Icon: CreditCard, color: "#16A34A",
    faqs: [
      { q: "¿Qué métodos de pago aceptan?", a: "Procesamos pagos con Mercado Pago (tarjetas y medios locales). Los precios se muestran en la moneda de tu región." },
      { q: "¿Dónde veo mis comprobantes?", a: "En Pagos y Facturación encontrarás el historial de tus pagos, órdenes y comprobantes." },
      { q: "¿Cómo cambio mi plan?", a: "Desde Planes y Membresías puedes comparar y actualizar tu plan (Básico, Pro, Premium) en cualquier momento." },
    ],
  },
];

function FaqItem({ q, a, color }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 py-3 text-left" data-testid="faq-toggle">
        <span className="text-sm font-semibold text-slate-800">{q}</span>
        <ChevronDown size={18} className={`text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} style={open ? { color } : {}} />
      </button>
      {open && <p className="pb-3 text-sm text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function CentroAyudaPage() {
  const { user } = useAuth();
  const esAdmin = user && user.role === "admin";
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const cats = CATEGORIAS.map((c) => ({
    ...c,
    faqs: c.faqs.filter((f) => !ql || f.q.toLowerCase().includes(ql) || f.a.toLowerCase().includes(ql)),
  })).filter((c) => c.faqs.length > 0);

  return (
    <div className="px-5 md:px-8 py-8 max-w-4xl" data-testid="centro-ayuda-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Ecosistema
      </Link>
      <h1 className="mt-4 text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-3">
        <LifeBuoy size={30} style={{ color: "#0891B2" }} /> Centro de Ayuda
      </h1>
      <p className="mt-2 text-slate-600">Preguntas frecuentes y guías del ecosistema ConstruLink.</p>

      <Link to="/manual" data-testid="ayuda-ir-manual" className="mt-6 block rounded-2xl overflow-hidden border border-slate-200 bg-gradient-to-br from-[#0B1F3A] to-[#1E3A8A] text-white p-5 hover:brightness-110 transition-all">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0"><BookOpen size={26} className="text-blue-100" /></span>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-200">Guía completa</p>
            <p className="text-lg font-extrabold leading-tight">Manual ConstruLink</p>
            <p className="text-sm text-blue-100">Conoce el ecosistema y sus 5 pilares paso a paso.</p>
          </div>
        </div>
      </Link>

      {esAdmin && (
      <Link to="/ayuda/manual-tecnico" data-testid="ayuda-ir-manual-tecnico" className="mt-3 block rounded-2xl overflow-hidden border border-slate-800 bg-gradient-to-br from-[#0B1220] to-[#111827] text-white p-5 hover:brightness-125 transition-all">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0"><FileCode2 size={26} className="text-emerald-300" /></span>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-300">Documentación técnica · solo administración</p>
            <p className="text-lg font-extrabold leading-tight">Manual Técnico</p>
            <p className="text-sm text-slate-300">Endpoints, módulos, rutas y arquitectura — sincronizado con el código.</p>
          </div>
        </div>
      </Link>
      )}

      <div className="mt-6 relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ayuda-buscar" placeholder="Busca una pregunta…"
          className="w-full rounded-2xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
      </div>

      <div className="mt-6 space-y-4">
        {cats.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No encontramos resultados. Prueba con otras palabras o contacta a Soporte.</p>}
        {cats.map((c) => {
          const Icon = c.Icon;
          return (
            <section key={c.key} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid={`ayuda-cat-${c.key}`}>
              <h2 className="font-bold text-slate-900 inline-flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${c.color}15`, color: c.color }}><Icon size={16} /></span>
                {c.label}
              </h2>
              <div className="mt-2">
                {c.faqs.map((f, i) => <FaqItem key={i} {...f} color={c.color} />)}
              </div>
            </section>
          );
        })}
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex items-center gap-3">
          <Headphones size={22} className="text-slate-500" />
          <p className="text-sm text-slate-600">¿No encontraste lo que buscabas?</p>
        </div>
        <Link to="/soporte" data-testid="ayuda-ir-soporte" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white font-semibold text-sm hover:brightness-110" style={{ backgroundColor: "#0891B2" }}>
          Contactar Soporte
        </Link>
      </div>
    </div>
  );
}
